/**
 * @fileoverview Petty Cash API Service
 * 
 * @description
 * Centralized API calls for Petty Cash module.
 * Handles funds, transactions, and dashboard data.
 * 
 * @version 1.0.0
 * @date 2026-02-06
 */

import { api } from '../../../api';

const BASE_URL = '/finance/petty-cash';
const RETRY_DELAYS_MS = [600, 1400, 2600];

const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const isRetryableGetError = (error) => {
  const status = error?.response?.status;
  const code = error?.code;
  const message = String(error?.message || '');

  if (error?.response == null) {
    return (
      code === 'ERR_NETWORK' ||
      code === 'ECONNABORTED' ||
      message.includes('Network') ||
      message.includes('timeout')
    );
  }

  return status === 408 || status === 429 || status >= 500;
};

const getWithRetry = async (url, config = {}, options = {}) => {
  const retries = Number.isInteger(options.retries) ? options.retries : RETRY_DELAYS_MS.length;
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await api.get(url, config);
    } catch (error) {
      lastError = error;
      if (attempt >= retries || !isRetryableGetError(error)) {
        throw error;
      }
      const delay = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
      await wait(delay);
    }
  }

  throw lastError;
};

/**
 * Dashboard & Statistics
 */
export const getDashboardStats = async () => {
  const response = await getWithRetry(`${BASE_URL}/dashboard`);
  return response.data;
};

export const getDashboardExpenseByAccount = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.from) queryParams.append('from', params.from);
  if (params.to) queryParams.append('to', params.to);
  if (params.display_currency) queryParams.append('display_currency', params.display_currency);
  if (Array.isArray(params.fund_ids)) {
    params.fund_ids
      .filter((id) => Number.isFinite(Number(id)))
      .forEach((id) => queryParams.append('fund_ids', String(id)));
  }
  const qs = queryParams.toString();
  const response = await getWithRetry(
    `${BASE_URL}/dashboard/expense-by-account${qs ? `?${qs}` : ''}`,
  );
  return response.data;
};

export const downloadExpenseByAccountPdf = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.from) queryParams.append('from', params.from);
  if (params.to) queryParams.append('to', params.to);
  if (params.display_currency) queryParams.append('display_currency', params.display_currency);
  if (Array.isArray(params.fund_ids)) {
    params.fund_ids
      .filter((id) => Number.isFinite(Number(id)))
      .forEach((id) => queryParams.append('fund_ids', String(id)));
  }
  const qs = queryParams.toString();
  const url = `${BASE_URL}/dashboard/expense-by-account/pdf${qs ? `?${qs}` : ''}`;
  const response = await getWithRetry(url, { responseType: 'blob' });
  const cd = response.headers['content-disposition'];
  let filename = 'expenses_by_account.pdf';
  if (cd && cd.includes('filename=')) {
    const m = cd.match(/filename="([^"]+)"|filename=([^;]+)/);
    const raw = m ? (m[1] || m[2] || '').trim() : '';
    if (raw) filename = raw.replace(/^["']|["']$/g, '');
  }
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

export const getDashboardExpenseByFund = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.from) queryParams.append('from', params.from);
  if (params.to) queryParams.append('to', params.to);
  if (params.display_currency) queryParams.append('display_currency', params.display_currency);
  if (Array.isArray(params.fund_ids)) {
    params.fund_ids
      .filter((id) => Number.isFinite(Number(id)))
      .forEach((id) => queryParams.append('fund_ids', String(id)));
  }
  const qs = queryParams.toString();
  const response = await getWithRetry(
    `${BASE_URL}/dashboard/expense-by-fund${qs ? `?${qs}` : ''}`,
  );
  return response.data;
};

/**
 * Fund Operations
 */
/**
 * Funds where the current user is custodian or assigned expense operator (GET /funds/mine).
 * Optional status e.g. 'OPEN' for mobile shortcuts.
 */
export const getMyPettyCashFunds = async (params = {}) => {
  const { status } = params;
  const queryParams = new URLSearchParams();
  if (status) queryParams.append('status', status);
  const qs = queryParams.toString();
  const url = qs ? `${BASE_URL}/funds/mine?${qs}` : `${BASE_URL}/funds/mine`;
  const response = await getWithRetry(url);
  return response.data;
};

export const getFunds = async (params = {}) => {
  const { skip = 0, limit = 50, status, search } = params;
  const queryParams = new URLSearchParams();
  queryParams.append('skip', skip);
  queryParams.append('limit', limit);
  if (status) queryParams.append('status', status);
  if (search) queryParams.append('search', search);
  
  const response = await getWithRetry(`${BASE_URL}/funds?${queryParams.toString()}`);
  return response.data;
};

export const getFund = async (fundId) => {
  const response = await getWithRetry(`${BASE_URL}/funds/${fundId}`);
  return response.data;
};

export const createFund = async (fundData) => {
  const response = await api.post(`${BASE_URL}/funds`, fundData);
  return response.data;
};

export const updateFund = async (fundId, fundData) => {
  const response = await api.put(`${BASE_URL}/funds/${fundId}`, fundData);
  return response.data;
};

export const deleteFund = async (fundId) => {
  await api.delete(`${BASE_URL}/funds/${fundId}`);
};

/**
 * Fund Status Transition Operations
 */
export const submitFundForReview = async (fundId) => {
  const response = await api.post(`${BASE_URL}/funds/${fundId}/submit-review`);
  return response.data;
};

export const approveFund = async (fundId) => {
  const response = await api.post(`${BASE_URL}/funds/${fundId}/approve`);
  return response.data;
};

export const rejectFund = async (fundId) => {
  const response = await api.post(`${BASE_URL}/funds/${fundId}/reject`);
  return response.data;
};

export const closeFund = async (fundId) => {
  const response = await api.post(`${BASE_URL}/funds/${fundId}/close`);
  return response.data;
};

export const reopenFund = async (fundId) => {
  const response = await api.post(`${BASE_URL}/funds/${fundId}/reopen`);
  return response.data;
};

/**
 * Fund Summary Operations (for audit/rendition)
 */
export const getFundSummary = async (fundId, params = {}) => {
  const { from, to } = params;
  const queryParams = new URLSearchParams();
  if (from) queryParams.append('from', from);
  if (to) queryParams.append('to', to);
  
  const queryString = queryParams.toString();
  const url = queryString 
    ? `${BASE_URL}/funds/${fundId}/summary?${queryString}`
    : `${BASE_URL}/funds/${fundId}/summary`;
  
  const response = await getWithRetry(url);
  return response.data;
};

/**
 * Download preliminary expense summary PDF (draft with logo, period, balances, lines, prepared-by).
 * Requires an active session; uses same date filters as summary preview.
 */
export const downloadPreliminaryExpenseSummaryPdf = async (fundId, params = {}) => {
  const { from, to } = params;
  const queryParams = new URLSearchParams();
  if (from) queryParams.append('from', from);
  if (to) queryParams.append('to', to);
  const qs = queryParams.toString();
  const url = qs
    ? `${BASE_URL}/funds/${fundId}/summary/preliminary-pdf?${qs}`
    : `${BASE_URL}/funds/${fundId}/summary/preliminary-pdf`;
  const response = await getWithRetry(url, { responseType: 'blob' });
  const cd = response.headers['content-disposition'];
  let filename = `preliminary_expenses_${fundId}.pdf`;
  if (cd && cd.includes('filename=')) {
    const m = cd.match(/filename="([^"]+)"|filename=([^;]+)/);
    const raw = m ? (m[1] || m[2] || '').trim() : '';
    if (raw) filename = raw.replace(/^["']|["']$/g, '');
  }
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

/**
 * Monthly Petty Cash Report (tenant-level aggregation)
 */
export const getMonthlyReport = async (year, month) => {
  const response = await getWithRetry(`${BASE_URL}/report/monthly?year=${year}&month=${month}`);
  return response.data;
};

/**
 * Saved fund period reports (snapshot of summary + transactions)
 */
export const savePettyCashReport = async (body) => {
  const response = await api.post(`${BASE_URL}/saved-reports`, body);
  return response.data;
};

export const listPettyCashSavedReports = async (params = {}) => {
  const { fundId, skip = 0, limit = 50 } = params;
  const queryParams = new URLSearchParams();
  const fid =
    fundId != null && String(fundId).trim() !== '' ? parseInt(String(fundId), 10) : NaN;
  if (Number.isFinite(fid) && fid >= 1) queryParams.append('fund_id', String(fid));
  queryParams.append('skip', skip);
  queryParams.append('limit', limit);
  const qs = queryParams.toString();
  const response = await getWithRetry(`${BASE_URL}/saved-reports?${qs}`);
  return response.data;
};

export const getPettyCashSavedReport = async (reportId) => {
  const response = await getWithRetry(`${BASE_URL}/saved-reports/${reportId}`);
  return response.data;
};

export const deletePettyCashSavedReport = async (reportId) => {
  await api.delete(`${BASE_URL}/saved-reports/${reportId}`);
};

/**
 * Transaction Operations
 */
export const getTransactions = async (fundId, params = {}) => {
  const { skip = 0, limit = 50, sort, transaction_type, status, date_from, date_to } = params;
  const queryParams = new URLSearchParams();
  queryParams.append('skip', skip);
  queryParams.append('limit', limit);
  if (sort) queryParams.append('sort', sort);
  if (transaction_type) queryParams.append('transaction_type', transaction_type);
  if (status) queryParams.append('status', status);
  if (date_from) queryParams.append('date_from', date_from);
  if (date_to) queryParams.append('date_to', date_to);
  
  const response = await getWithRetry(`${BASE_URL}/funds/${fundId}/transactions?${queryParams.toString()}`);
  return response.data;
};

export const createTransaction = async (fundId, transactionData) => {
  const response = await api.post(`${BASE_URL}/funds/${fundId}/transactions`, transactionData);
  return response.data;
};

export const getTransaction = async (transactionId) => {
  const response = await getWithRetry(`${BASE_URL}/transactions/${transactionId}`);
  return response.data;
};

export const updateTransaction = async (transactionId, transactionData) => {
  const response = await api.put(`${BASE_URL}/transactions/${transactionId}`, transactionData);
  return response.data;
};

export const deleteTransaction = async (transactionId) => {
  await api.delete(`${BASE_URL}/transactions/${transactionId}`);
};

/**
 * Distinct vendor / payee names from past petty cash transactions (autocomplete).
 * @param {string} [q] - Optional partial name; omit to get most frequent vendors.
 * @param {{ fundId?: string|number, fund_id?: string|number, limit?: number }} [options] - Optional fund id to scope to one cash box only; omit for all funds in the tenant. Optional limit (default 50, max 100 on API).
 */
export const getVendorSuggestions = async (q, options = {}) => {
  const fundDbKey = options.fundId ?? options.fund_id;
  const params = new URLSearchParams();
  if (q != null && String(q).trim()) {
    params.append('q', String(q).trim());
  }
  if (fundDbKey != null && String(fundDbKey).trim() !== '') {
    params.append('fund_id', String(fundDbKey));
  }
  if (options.limit != null && Number.isFinite(Number(options.limit))) {
    params.append('limit', String(Math.min(100, Math.max(1, Math.floor(Number(options.limit))))));
  }
  const qs = params.toString();
  const url = qs
    ? `${BASE_URL}/vendor-suggestions?${qs}`
    : `${BASE_URL}/vendor-suggestions`;
  const response = await getWithRetry(url);
  return response.data;
};

/**
 * Receipt OCR (Amazon Textract AnalyzeExpense) — pre-fill amount / vendor / currency
 */
export const analyzeReceiptExpense = async (file) => {
  const formData = new FormData();
  formData.append('receipt', file);
  const response = await api.post(
    `${BASE_URL}/receipts/analyze-expense`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000,
    }
  );
  return response.data;
};

/**
 * Receipt Image Operations
 */
export const uploadReceiptImage = async (transactionId, file) => {
  const formData = new FormData();
  formData.append('receipt', file);
  
  const response = await api.post(
    `${BASE_URL}/transactions/${transactionId}/receipt-image`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

export const deleteReceiptImage = async (transactionId) => {
  const response = await api.delete(`${BASE_URL}/transactions/${transactionId}/receipt-image`);
  return response.data;
};

/**
 * User Operations (for custodian selection)
 */
export const getAvailableUsers = async (search = '', purpose = 'custodian') => {
  const q = new URLSearchParams();
  if (search) q.append('search', search);
  if (purpose) q.append('purpose', purpose);
  const qs = q.toString();
  const response = await getWithRetry(`${BASE_URL}/users${qs ? `?${qs}` : ''}`);
  return response.data;
};

/**
 * Chart of Accounts Integration
 * Uses existing COA endpoints
 */
export const getCOAAccounts = async (templateId = null) => {
  // Backend requires template_id; missing → 422 Unprocessable Entity
  const tid = templateId != null && String(templateId).trim() !== ''
    ? Number(templateId)
    : NaN;
  if (!Number.isFinite(tid)) {
    return [];
  }
  const params = new URLSearchParams();
  params.append('template_id', String(tid));
  params.append('status', 'active');
  params.append('flat', 'true');  // Get all accounts, not just root level

  const response = await getWithRetry(`/finance/coa/accounts?${params.toString()}`);
  return response.data;
};

export const getCOAAccountsTree = async (templateId) => {
  const response = await getWithRetry(`/finance/coa/templates/${templateId}/tree`);
  return response.data;
};

export const getCOATemplates = async () => {
  const response = await getWithRetry('/finance/coa/templates');
  return response.data;
};

/**
 * Transfer Operations
 */
export const createTransfer = async (transferData) => {
  const response = await api.post(`${BASE_URL}/transfers`, transferData);
  return response.data;
};

export const getOpenFunds = async () => {
  const response = await getWithRetry(`${BASE_URL}/funds`, {
    params: { status: 'OPEN', limit: 100 }
  });
  return response.data;
};

/**
 * Utility Functions
 */
export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

export const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const FUND_STATUS = {
  OPEN: 'OPEN',
  IN_REVIEW: 'IN_REVIEW',
  APPROVED: 'APPROVED',
  CLOSED: 'CLOSED',
  SUSPENDED: 'SUSPENDED'
};

export const TRANSACTION_TYPES = {
  DISBURSEMENT: 'DISBURSEMENT',
  REPLENISHMENT: 'REPLENISHMENT',
  ADJUSTMENT: 'ADJUSTMENT',
  TRANSFER: 'TRANSFER'
};

export const getStatusBadgeVariant = (status) => {
  switch (status) {
    case 'OPEN': return 'success';
    case 'IN_REVIEW': return 'warning';
    case 'APPROVED': return 'info';
    case 'CLOSED': return 'secondary';
    case 'SUSPENDED': return 'danger';
    case 'PENDING': return 'warning';
    case 'REJECTED': return 'danger';
    default: return 'light';
  }
};

export default {
  getDashboardStats,
  getDashboardExpenseByAccount,
  downloadExpenseByAccountPdf,
  getDashboardExpenseByFund,
  getFunds,
  getFund,
  getMyPettyCashFunds,
  createFund,
  updateFund,
  deleteFund,
  submitFundForReview,
  approveFund,
  rejectFund,
  closeFund,
  reopenFund,
  getFundSummary,
  downloadPreliminaryExpenseSummaryPdf,
  getMonthlyReport,
  savePettyCashReport,
  listPettyCashSavedReports,
  getPettyCashSavedReport,
  deletePettyCashSavedReport,
  getTransactions,
  createTransaction,
  getTransaction,
  updateTransaction,
  deleteTransaction,
  getVendorSuggestions,
  uploadReceiptImage,
  deleteReceiptImage,
  getAvailableUsers,
  getCOAAccounts,
  getCOAAccountsTree,
  getCOATemplates,
  createTransfer,
  getOpenFunds,
  formatCurrency,
  formatDate,
  FUND_STATUS,
  TRANSACTION_TYPES,
  getStatusBadgeVariant
};
